import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Send, ArrowLeft, Paperclip, X, FileText, Check, CheckCheck, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import notificationService from '@/lib/notifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InquiryReplyPage() {
  const { id } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  useEffect(() => {
    // Initialize notifications
    notificationService.init();
    checkNotificationPermission();
    
    loadInquiry();
    
    // Auto-refresh every 5 seconds for real-time chat
    refreshIntervalRef.current = setInterval(() => {
      loadInquiry(true);
    }, 5000);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [inquiry?.messages?.length]);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const enableNotifications = async () => {
    const granted = await notificationService.requestPermission();
    setNotificationsEnabled(granted);
    if (granted) {
      toast.success('Benachrichtigungen aktiviert!');
    } else {
      toast.error('Benachrichtigungen wurden abgelehnt');
    }
  };

  const loadInquiry = async (silent = false) => {
    try {
      const response = await axios.get(`${API_URL}/api/inquiries/${id}/public`);
      const newInquiry = response.data;
      
      // Check for new messages and notify
      if (silent && newInquiry.messages && lastMessageCountRef.current > 0) {
        const newCount = newInquiry.messages.length;
        if (newCount > lastMessageCountRef.current) {
          // Find new admin messages
          const newMessages = newInquiry.messages.slice(lastMessageCountRef.current);
          const newAdminMessages = newMessages.filter(m => m.sender === 'admin');
          
          if (newAdminMessages.length > 0 && notificationsEnabled) {
            const lastMsg = newAdminMessages[newAdminMessages.length - 1];
            notificationService.showNotification(
              'Neue Antwort von Tim Automobile',
              lastMsg.message.substring(0, 100),
              `/inquiry/${id}/reply`,
              id
            );
          }
        }
      }
      
      lastMessageCountRef.current = newInquiry.messages?.length || 0;
      setInquiry(newInquiry);
    } catch (error) {
      console.error('Error loading inquiry:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/api/media/public-upload`, formData);
        
        setAttachments(prev => [...prev, {
          url: response.data.url,
          name: response.data.original_name,
          type: response.data.mime_type
        }]);
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/inquiries/${id}/customer-reply`, {
        message: message.trim() || '(Anhang)',
        attachments: attachments.map(a => a.url)
      });
      
      toast.success('Nachricht gesendet!');
      setMessage('');
      setAttachments([]);
      await loadInquiry();
      
    } catch (error) {
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setSending(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const isImage = (url) => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderReadStatus = (msg) => {
    if (msg.sender === 'customer') {
      if (msg.read_by_admin) {
        return <CheckCheck size={14} className="text-blue-500" />;
      } else {
        return <Check size={14} className="text-white/50" />;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 pb-24 flex items-center justify-center">
          <div className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 pb-24 text-center">
          <h1 className="font-heading text-3xl mb-4">Anfrage nicht gefunden</h1>
          <p className="text-muted-foreground mb-8">Diese Anfrage existiert nicht oder wurde gelöscht.</p>
          <Link to="/" className="btn-primary">Zur Startseite</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <div className="flex-1 pt-24 pb-4 flex flex-col">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 w-full flex-1 flex flex-col">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft size={16} />
                <span className="text-sm">Startseite</span>
              </Link>
              
              {/* Notification Toggle */}
              <button
                onClick={enableNotifications}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                  notificationsEnabled 
                    ? 'bg-green-500/10 text-green-500' 
                    : 'bg-card border border-border text-muted-foreground hover:border-primary'
                }`}
                title={notificationsEnabled ? 'Benachrichtigungen aktiv' : 'Benachrichtigungen aktivieren'}
              >
                {notificationsEnabled ? <Bell size={16} /> : <BellOff size={16} />}
                <span className="hidden sm:inline">
                  {notificationsEnabled ? 'Aktiv' : 'Benachrichtigen'}
                </span>
              </button>
            </div>
            
            <h1 className="font-heading text-2xl">Ihre Konversation</h1>
            {inquiry.car_info && (
              <p className="text-sm text-primary mt-1">
                {inquiry.car_info.year} {inquiry.car_info.brand} {inquiry.car_info.model}
              </p>
            )}
          </div>

          {/* Chat Container */}
          <div className="bg-card border border-border flex-1 flex flex-col" style={{ minHeight: '400px', maxHeight: 'calc(100vh - 280px)' }}>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {inquiry.messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                >
                  <div 
                    className={`max-w-[85%] rounded-lg p-4 ${
                      msg.sender === 'admin' 
                        ? 'bg-gold/10 text-foreground' 
                        : 'bg-foreground text-background'
                    }`}
                  >
                    <p className={`text-xs mb-1 ${msg.sender === 'admin' ? 'text-primary' : 'opacity-60'}`}>
                      {msg.sender === 'admin' ? 'Tim Automobile' : 'Sie'}
                    </p>
                    <p className="whitespace-pre-wrap break-words text-sm">{msg.message}</p>
                    
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {msg.attachments.map((url, i) => (
                          <a
                            key={i}
                            href={getFullUrl(url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            {isImage(url) ? (
                              <img 
                                src={getFullUrl(url)} 
                                alt="Anhang" 
                                className="max-w-full max-h-48 rounded"
                              />
                            ) : (
                              <span className="flex items-center gap-2 text-sm text-primary underline">
                                <FileText size={14} />
                                Anhang öffnen
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                    )}
                    
                    <div className={`flex items-center gap-2 mt-2 ${msg.sender === 'customer' ? 'justify-end' : ''}`}>
                      <p className={`text-xs ${msg.sender === 'admin' ? 'text-muted-foreground' : 'opacity-50'}`}>
                        {formatDate(msg.timestamp)}
                      </p>
                      {renderReadStatus(msg)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="px-4 py-2 border-t border-border bg-muted">
                <div className="flex flex-wrap gap-2">
                  {attachments.map((att, index) => (
                    <div key={index} className="relative group">
                      {isImage(att.url) ? (
                        <img 
                          src={getFullUrl(att.url)} 
                          alt={att.name}
                          className="h-16 w-16 object-cover rounded"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-card rounded flex items-center justify-center border border-border">
                          <FileText size={24} className="text-muted-foreground" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="border-t border-border p-3">
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*,.pdf"
                  multiple
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-3 border border-border hover:border-primary hover:text-primary transition-colors flex-shrink-0"
                >
                  {uploading ? <div className="spinner w-5 h-5" /> : <Paperclip size={20} />}
                </button>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="flex-1 border border-border px-4 py-3 focus:border-foreground focus:outline-none text-sm bg-transparent"
                  placeholder="Nachricht schreiben..."
                  data-testid="reply-message"
                />
                <Button
                  type="submit"
                  className="btn-primary px-4 flex-shrink-0"
                  disabled={sending || (!message.trim() && attachments.length === 0)}
                  data-testid="reply-submit"
                >
                  {sending ? <div className="spinner w-5 h-5" /> : <Send size={20} />}
                </Button>
              </div>
            </form>
          </div>

          {/* Info text */}
          <p className="text-xs text-muted-foreground text-center mt-3">
            Automatische Aktualisierung alle 30 Sekunden
          </p>
        </div>
      </div>
    </div>
  );
}
